import React, { useEffect, useRef, useState } from 'react';
import {ContractExceptionFields, ContractExceptionDetails, DOCUMENT_EXPIRY_EXCEPTION_SCOPE} from './ContractExceptionFields';
import { KTSVG } from '../../../../_metronic/helpers';
import { updateAssignment } from '../core/_requests';
import { toast } from 'react-toastify';
import { SignOnOffRecord } from '../core/_requests';
import { showErrorToast } from '../../../shared/utils/apiErrorMessage';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    recordData: any;
    onUpdate: (updatedRecord: SignOnOffRecord) => void;
}

const EditAssignmentModal: React.FC<Props> = ({ isOpen, onClose, recordData, onUpdate }) => {
    const [signOnDate, setSignOnDate] = useState(recordData.signOnDate || '');
    const [dateOfAgreement, setDateOfAgreement] = useState(recordData.dateOfAgreement || '');
    const [contractEndDate, setContractEndDate] = useState(recordData.contractEndDate || '');
    const [signOnTime, setSignOnTime] = useState(recordData.signOnTime || '');
    const [portSignOn, setPortSignOn] = useState(recordData.portSignOn || '');
    const [signOffDate, setSignOffDate] = useState(recordData.signOffDate || '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submitLock = useRef(false);
    const [contractBufferException, setContractBufferException] = useState(false);
    const [contractBufferExceptionReason, setContractBufferExceptionReason] = useState('');
    const [exceptionTouched, setExceptionTouched] = useState(false);
    useEffect(() => {
        setSignOnDate(recordData.signOnDate || '');
        setDateOfAgreement(recordData.dateOfAgreement || '');
        setContractEndDate(recordData.contractEndDate || '');
        setSignOnTime(recordData.signOnTime || '');
        setPortSignOn(recordData.portSignOn || '');
        setSignOffDate(recordData.signOffDate || '');
    }, [recordData.id, recordData.signOnDate, recordData.dateOfAgreement, recordData.contractEndDate,
        recordData.signOnTime, recordData.portSignOn, recordData.signOffDate, isOpen]);
    useEffect(() => {
        // Earlier buffer-only consent must not select the broader expiry waiver.
        const saved = Boolean(recordData.contractBufferException)
            && recordData.contractBufferExceptionDocuments?.startsWith(DOCUMENT_EXPIRY_EXCEPTION_SCOPE)
            && contractEndDate === recordData.contractEndDate && signOnDate === recordData.signOnDate;
        setContractBufferException(Boolean(saved));
        setContractBufferExceptionReason(saved ? recordData.contractBufferExceptionReason || '' : '');
        setExceptionTouched(false);
    }, [recordData.id, recordData.contractBufferException, recordData.contractBufferExceptionDocuments,
        recordData.contractBufferExceptionReason, recordData.contractEndDate, recordData.signOnDate,
        contractEndDate, signOnDate, isOpen]);

    const isPlannedSignedOff = recordData.status === "PLANNED_SIGN_OFF";

    const handleUpdate = async () => {
        if (submitLock.current) return;
        if (contractBufferException && (!contractBufferExceptionReason.trim() || contractBufferExceptionReason.trim().length > 1000)) {
            toast.warn('Enter document exception remarks of 1 to 1000 characters.');
            return;
        }
        if (!isPlannedSignedOff) {
            if (!signOnDate) {
                toast.warn('Please select sign-on date.');
                return;
            }
            if (!signOnTime) {
                toast.warn('Please select sign-on time.');
                return;
            }
            if (!portSignOn?.trim()) {
                toast.warn('Please enter sign-on port.');
                return;
            }
        } else {
            if (!signOffDate) {
                toast.warn('Please select sign-off date.');
                return;
            }
        }

        if (signOnDate && contractEndDate && new Date(contractEndDate) < new Date(signOnDate)) {
            toast.warn('Contract end date cannot be earlier than sign-on date.');
            return;
        }

        submitLock.current = true;
        setIsSubmitting(true);
        try {
            const updated = await updateAssignment(recordData.id, { 
                signOnDate: isPlannedSignedOff ? undefined : signOnDate,
                dateOfAgreement,
                contractEndDate,
                signOnTime: isPlannedSignedOff ? undefined : signOnTime,
                signOffDate: isPlannedSignedOff ? signOffDate : undefined,
                portSignOn: portSignOn?.trim(),
                contractBufferException: exceptionTouched ? contractBufferException : undefined,
                contractBufferExceptionReason: exceptionTouched && contractBufferException ? contractBufferExceptionReason.trim() : undefined
            });
            onUpdate(updated);
            onClose();
            toast.success('Assignment updated successfully');
        } catch (err: any) {
            console.error('Error updating assignment:', err);
            showErrorToast(err, 'Failed to update assignment.', 'update');
        } finally {
            submitLock.current = false;
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="custom-modal-header d-flex justify-content-between align-items-center">
                    <h5>Edit Crew Assignment</h5>
                    <button className="close-btn" onClick={onClose} disabled={isSubmitting}>
                        <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
                    </button>
                </div>
                <div className="custom-modal-body">
                    <div className="row">
                        {!isPlannedSignedOff && (
                            <>
                                <div className="col-md-6 mb-3">
                                    <label className="modal_label">Sign On Date <span className="text-danger">*</span></label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={signOnDate}
                                        onChange={(e) => setSignOnDate(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="modal_label">Sign On Time <span className="text-danger">*</span></label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={signOnTime}
                                        onChange={(e) => setSignOnTime(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="modal_label">Port Sign On <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={portSignOn}
                                        onChange={(e) => setPortSignOn(e.target.value)}
                                        placeholder="Enter port of sign-on"
                                        required
                                    />
                                </div>
                            </>
                        )}
                        {isPlannedSignedOff && (
                            <div className="col-md-6 mb-3">
                                <label className="modal_label">Sign Off Date <span className="text-danger">*</span></label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={signOffDate}
                                    onChange={(e) => setSignOffDate(e.target.value)}
                                    required
                                />
                            </div>
                        )}
                        <div className="col-md-6 mb-3">
                            <label className="modal_label">Date of Agreement</label>
                            <input
                                type="date"
                                className="form-control"
                                value={dateOfAgreement}
                                onChange={(e) => setDateOfAgreement(e.target.value)}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="modal_label">Contract End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={contractEndDate}
                                onChange={(e) => setContractEndDate(e.target.value)}
                            />
                        </div>
                        <ContractExceptionDetails evidence={recordData} />
                        <ContractExceptionFields checked={contractBufferException} reason={contractBufferExceptionReason}
                            onChecked={value => {setContractBufferException(value); setExceptionTouched(true);}}
                            onReason={value => {setContractBufferExceptionReason(value); setExceptionTouched(true);}} disabled={isSubmitting} />
                    </div>
                </div>
                <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn_secondary" onClick={onClose} disabled={isSubmitting}>Cancel</button>
                    <button className="btn btn_success" onClick={handleUpdate} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Saving...
                            </>
                        ) : (
                            'Save Changes'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditAssignmentModal;
